import axios from 'axios';
import type {
  AdminUser,
  User,
  EmployeeSuperValidation,
  XeroEmployee,
  XeroPayrollCalendar,
  XeroCalendarNextPeriod,
  TenantExpectedPeriod,
  XeroAccountCode,
  SavedAccountCode,
  UploadResult,
  ImportLine,
  PayRunResult,
  QuickSuperSummary,
  QuickSuperMember,
  QuickSuperMatch,
  QuickSuperMembersImportResult,
  PastRun,
  TransactionReport,
} from '../types';

const api = axios.create({
  baseURL: '/api',
  withCredentials: true,
  headers: { 'Content-Type': 'application/json' },
});

// Centralised 401 handling: any authenticated API call returning 401 means the
// backend has destroyed contractors.sid (parent InvoicePolish session is gone).
// Broadcast an event so the app can clear all local state and show login.
// Excludes /auth/me (the app's bootstrap path which handles 401 itself) and
// the Stripe webhook (which never runs through the client).
export const AUTH_UNAUTHORIZED_EVENT = 'contractors:unauthorized';
// Fired when ANY Xero payroll call comes back 403 with code `xero_payroll_unauthorized`.
// Means: the InvoicePolish-issued Xero access token does NOT carry payroll scopes.
// Surface a banner asking the user to reconnect Xero from InvoicePolish.
// detail.reconnectUrl links to the InvoicePolish Xero integration page.
export const XERO_PAYROLL_UNAUTHORIZED_EVENT = 'contractors:xero-payroll-unauthorized';
export interface XeroPayrollUnauthorizedDetail {
  message: string;
  reconnectUrl: string | null;
}
// Fired when a backend route returns 409 with code `xero_org_disconnected`.
// Means: the selected Xero tenant was removed from InvoicePolish while this
// session was active. App must immediately reconcile — clear stale org state,
// re-fetch /xero/status, and enter the disconnected UI state.
export const XERO_ORG_DISCONNECTED_EVENT = 'contractors:xero-org-disconnected';
export interface XeroOrgDisconnectedDetail {
  message: string;
}
api.interceptors.response.use(
  (r) => r,
  (error) => {
    const status = error?.response?.status;
    const url: string = error?.config?.url ?? '';
    const data = error?.response?.data;
    if (status === 401 && !url.endsWith('/auth/me')) {
      window.dispatchEvent(new CustomEvent(AUTH_UNAUTHORIZED_EVENT));
    }
    if (status === 403 && data?.code === 'xero_payroll_unauthorized') {
      window.dispatchEvent(
        new CustomEvent<XeroPayrollUnauthorizedDetail>(XERO_PAYROLL_UNAUTHORIZED_EVENT, {
          detail: {
            message: typeof data.error === 'string' ? data.error : 'Xero payroll access has not been authorised.',
            reconnectUrl: typeof data.reconnectUrl === 'string' ? data.reconnectUrl : null,
          },
        })
      );
    }
    if (status === 409 && data?.code === 'xero_org_disconnected') {
      window.dispatchEvent(
        new CustomEvent<XeroOrgDisconnectedDetail>(XERO_ORG_DISCONNECTED_EVENT, {
          detail: {
            message: typeof data.message === 'string'
              ? data.message
              : 'This Xero organisation has been disconnected.',
          },
        })
      );
    }
    return Promise.reject(error);
  }
);

// Account — name and password management, proxied through InvoicePolish.
export const accountApi = {
  get: () => api.get<{ name: string | null; email: string | null }>('/account'),
  updateName: (name: string) =>
    api.patch<{ ok: boolean; name: string }>('/account', { name }),
  changePassword: (currentPassword: string, newPassword: string) =>
    api.post<{ ok: boolean }>('/account/change-password', { currentPassword, newPassword }),
};

// Auth — sign-in is a top-level browser redirect (SSO via InvoicePolish).
export const authApi = {
  logout: () => api.post<{ ok: boolean; logoutUrl: string | null }>('/auth/logout'),
  me: () => api.get<{ authenticated: boolean; user?: User }>('/auth/me'),
  config: () => api.get<{ invoicepolishBaseUrl: string | null }>('/auth/config'),
};

// Xero — orgs come from InvoicePolish; no connect/disconnect from this app.
export const xeroApi = {
  connectUrl: () => api.get<{ url: string }>('/xero/connect-url'),
  status: () =>
    api.get<{
      connected: boolean;
      activeTenantId: string | null;
      tenants: Array<{
        tenantId: string;
        tenantName: string;
        isActive: boolean;
        hasPayrollAccess: boolean;
        connectedAt?: string | null;
      }>;
    }>('/xero/status'),
  selectTenant: (tenantId: string) => api.post('/xero/select-tenant', { tenantId }),
  disconnect: (tenantId: string) => api.post('/xero/disconnect', { tenantId }),
  employees: () => api.get<{ employees: XeroEmployee[] }>('/xero/employees'),
  validateSelectedEmployees: (employeeIds: string[], forceRefresh = false) =>
    api.post<{ employees: EmployeeSuperValidation[] }>('/xero/payroll/validate-selected-employees', {
      employeeIds,
      forceRefresh,
    }),
  payrollCalendars: () => api.get<{ calendars: XeroPayrollCalendar[] }>('/xero/payroll-calendars'),
  testPayroll: () => api.get('/xero/test-payroll'),
};

// QuickSuper — registered members, remembered name matches, contribution file
export const quickSuperApi = {
  getState: (importId: number) =>
    api.get<{ members: QuickSuperMember[]; matches: Record<string, QuickSuperMatch> }>(
      `/quicksuper/state?importId=${importId}`
    ),
  setMapping: (importId: number, lineId: number, memberId: number | null) =>
    api.put<{ match: QuickSuperMatch }>('/quicksuper/mapping', { importId, lineId, memberId }),
  // Resolves with the CSV text; rejects with the server's message.
  downloadContributionFile: async (params: {
    importId: number;
    lineIds: number[];
    periodStart: string;
    periodEnd: string;
  }): Promise<{ blob: Blob; filename: string }> => {
    try {
      const res = await api.post('/quicksuper/contribution-file', params, { responseType: 'blob' });
      const disposition = String(res.headers['content-disposition'] ?? '');
      const filename = /filename="([^"]+)"/.exec(disposition)?.[1] ?? 'quicksuper-contributions.csv';
      return { blob: res.data as Blob, filename };
    } catch (err) {
      const data = (err as { response?: { data?: Blob } })?.response?.data;
      if (data instanceof Blob) {
        try {
          throw new Error(JSON.parse(await data.text()).error ?? 'Could not build the file');
        } catch (inner) {
          if (inner instanceof Error && !(inner instanceof SyntaxError)) throw inner;
        }
      }
      throw new Error('Could not build the file');
    }
  },
};

// Import
export const importApi = {
  upload: (file: File) => {
    const form = new FormData();
    form.append('file', file);
    return api.post<UploadResult | QuickSuperMembersImportResult>('/import/upload', form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  getXeroSelection: (importId: number) =>
    api.get<{ saved: boolean; lineIds: number[] }>(`/import/${importId}/xero-selection`),
  saveXeroSelection: (importId: number, lineIds: number[]) =>
    api.put<{ ok: true }>(`/import/${importId}/xero-selection`, { lineIds }),
  getLines: (importId: number) =>
    api.get<{ import: UploadResult['import']; lines: ImportLine[] }>(`/import/${importId}/lines`),
  updateLine: (
    importId: number,
    lineId: number,
    updates: { superRate?: number; xeroEmployeeId?: string; xeroEmployeeName?: string }
  ) => api.patch<{ line: ImportLine }>(`/import/${importId}/lines/${lineId}`, updates),
};

// Pay run
export const payrunApi = {
  create: (params: {
    importId: number;
    selectedLineIds: number[];
    payrollCalendarId: string;
    payPeriodStartDate: string;
    payPeriodEndDate: string;
    paymentDate: string;
  }) => api.post<PayRunResult>('/payrun/create', params),
  list: () => api.get<{ payruns: PastRun[] }>('/payrun'),
  delete: (id: number) => api.delete<{ ok: true }>(`/payrun/${id}`),
  quickSuper: (id: number) => api.get<QuickSuperSummary>(`/payrun/${id}/quicksuper`),
  finalise: (id: number) => api.post<{ payrun: PastRun; xeroTotal: number; quickSuperTotal: number; grandTotal: number }>(`/payrun/${id}/finalise`),
  csvUrl: (id: number) => `/api/payrun/${id}/csv`,
  pdfUrl: (id: number) => `/api/payrun/${id}/pdf`,
  sourceFileUrl: (id: number) => `/api/payrun/${id}/source`,
  expectedPeriod: (payrollCalendarId: string) =>
    api.get<XeroCalendarNextPeriod>(
      `/payrun/expected-period?payrollCalendarId=${encodeURIComponent(payrollCalendarId)}`
    ),
  expectedPeriodForTenant: () =>
    api.get<TenantExpectedPeriod>('/payrun/expected-period-for-tenant'),
};

// Accounting — automated Xero report workflow
export const accountingApi = {
  getXeroAccounts: () =>
    api.get<{ accounts: XeroAccountCode[] }>('/accounting/xero-accounts'),
  getSavedAccountCodes: () =>
    api.get<{ accountCodes: SavedAccountCode[] }>('/accounting/saved-account-codes'),
  saveAccountCode: (accountCode: string, accountName: string | null) =>
    api.post<{ accountCode: SavedAccountCode; duplicate?: boolean }>(
      '/accounting/saved-account-codes',
      { accountCode, accountName }
    ),
  deleteAccountCode: (id: number) =>
    api.delete<{ ok: true }>(`/accounting/saved-account-codes/${id}`),
  calculateSuper: (startDate: string, endDate: string) =>
    api.post<UploadResult>('/accounting/calculate-super', { startDate, endDate }),
  previewReport: (startDate: string, endDate: string) =>
    api.post<{ report: TransactionReport }>('/accounting/preview-report', { startDate, endDate }),
};

// Stripe
export const stripeApi = {
  subscription: () => api.get<{ active: boolean; status?: string; currentPeriodEnd?: string }>('/stripe/subscription'),
  createCheckout: (priceId: string) => api.post<{ url: string }>('/stripe/create-checkout', { priceId }),
};

export default api;

// Admin — Contractors.x users, trials and connected organisations (owner only).
export const adminApi = {
  users: () => api.get<{ users: AdminUser[] }>('/admin/users'),
  extendTrial: (userId: string, days: number) =>
    api.post<{ trialEndsAt: string }>(`/admin/users/${encodeURIComponent(userId)}/trial`, { days }),
  setTrialOrgs: (userId: string, maxOrgs: number) =>
    api.post<{ trialMaxOrgs: number }>(`/admin/users/${encodeURIComponent(userId)}/trial-orgs`, { maxOrgs }),
  togglePause: (userId: string) =>
    api.post<{ accountPaused: boolean }>(`/admin/users/${encodeURIComponent(userId)}/pause`),
  disconnectOrg: (userId: string, connectionId: string) =>
    api.post<{ success: boolean }>(`/admin/users/${encodeURIComponent(userId)}/disconnect-org`, { connectionId }),
  deleteUser: (userId: string) => api.delete<{ success: boolean }>(`/admin/users/${encodeURIComponent(userId)}`),
};
